/**
 * Company Default Fees Tab
 *
 * Manages default fees for a company with add, edit, and delete functionality.
 */

import { DollarSign, Percent, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  useCompanyDefaultFees,
  useCreateDefaultFee,
  useDeleteDefaultFee,
  useUpdateDefaultFee,
} from '../hooks';
import type { CDSection, CompanyDefaultFee, CreateDefaultFeeInput, FeePaidBy } from '../types';

interface Props {
  companyId: string;
}

const PAID_BY_OPTIONS: { value: FeePaidBy; label: string }[] = [
  { value: 'buyer', label: 'Buyer' },
  { value: 'seller', label: 'Seller' },
  { value: 'split', label: 'Split' },
];

const CD_SECTION_OPTIONS: { value: CDSection; label: string }[] = [
  { value: 'A', label: 'A - Origination Charges' },
  { value: 'B', label: 'B - Services You Cannot Shop For' },
  { value: 'C', label: 'C - Services You Can Shop For' },
  { value: 'D', label: 'D - Total Loan Costs' },
  { value: 'E', label: 'E - Taxes and Other Government Fees' },
  { value: 'F', label: 'F - Prepaids' },
  { value: 'G', label: 'G - Initial Escrow Payment at Closing' },
  { value: 'H', label: 'H - Other' },
];

const PERCENTAGE_OF_OPTIONS = [
  { value: 'loan_amount', label: 'Loan Amount' },
  { value: 'sale_price', label: 'Sale Price' },
];

const INITIAL_FORM: CreateDefaultFeeInput = {
  fee_name: '',
  amount: undefined,
  is_percentage: false,
  percentage_of: undefined,
  paid_by: 'buyer',
  cd_section: undefined,
  description: '',
  is_active: true,
};

function formatCurrency(amount: string | number | undefined): string {
  if (amount === undefined || amount === null) return '-';
  const num = typeof amount === 'string' ? Number.parseFloat(amount) : amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(num);
}

function getPaidByBadgeClass(paidBy: FeePaidBy): string {
  const classes: Record<FeePaidBy, string> = {
    buyer: 'bg-blue-100 text-blue-800',
    seller: 'bg-green-100 text-green-800',
    split: 'bg-purple-100 text-purple-800',
  };
  return classes[paidBy] || 'bg-gray-100 text-gray-800';
}

export default function CompanyDefaultFeesTab({ companyId }: Props) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFee, setEditingFee] = useState<CompanyDefaultFee | null>(null);
  const [formData, setFormData] = useState<CreateDefaultFeeInput>(INITIAL_FORM);

  const { data: fees, isLoading } = useCompanyDefaultFees(companyId);
  const createFee = useCreateDefaultFee();
  const updateFee = useUpdateDefaultFee();
  const deleteFee = useDeleteDefaultFee();

  const handleOpenCreate = () => {
    setEditingFee(null);
    setFormData(INITIAL_FORM);
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (fee: CompanyDefaultFee) => {
    setEditingFee(fee);
    setFormData({
      fee_name: fee.fee_name,
      amount: fee.amount ? Number.parseFloat(fee.amount) : undefined,
      is_percentage: fee.is_percentage,
      percentage_of: fee.percentage_of || undefined,
      paid_by: fee.paid_by,
      cd_section: fee.cd_section || undefined,
      description: fee.description || '',
      is_active: fee.is_active,
    });
    setIsDialogOpen(true);
  };

  const handleClose = () => {
    setIsDialogOpen(false);
    setEditingFee(null);
    setFormData(INITIAL_FORM);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingFee) {
        await updateFee.mutateAsync({
          companyId,
          feeId: editingFee.id,
          data: formData,
        });
      } else {
        await createFee.mutateAsync({
          companyId,
          data: formData,
        });
      }
      handleClose();
    } catch (err) {
      console.error('Failed to save fee:', err);
    }
  };

  const handleDelete = async (feeId: string) => {
    if (!confirm('Are you sure you want to delete this default fee?')) return;

    try {
      await deleteFee.mutateAsync({ companyId, feeId });
    } catch (err) {
      console.error('Failed to delete fee:', err);
    }
  };

  const handleInputChange = (
    field: keyof CreateDefaultFeeInput,
    value: string | number | boolean | undefined,
  ) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      // Clear percentage_of if switching to flat amount
      if (field === 'is_percentage' && !value) {
        updated.percentage_of = undefined;
      }
      return updated;
    });
  };

  if (isLoading) {
    return <div className="py-8 text-center text-gray-500">Loading default fees...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Default Fees</h3>
        <Button onClick={handleOpenCreate} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Fee
        </Button>
      </div>

      {!fees?.length ? (
        <Card>
          <CardContent className="py-8 text-center">
            <DollarSign className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">No default fees configured</p>
            <Button variant="link" onClick={handleOpenCreate} className="mt-2">
              Add the first default fee
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {fees.map((fee) => (
            <Card key={fee.id} className={!fee.is_active ? 'opacity-60' : ''}>
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{fee.fee_name}</span>
                      {!fee.is_active && (
                        <Badge variant="secondary" className="text-xs">
                          Inactive
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-sm">
                      <span className="flex items-center gap-1">
                        {fee.is_percentage ? (
                          <>
                            <Percent className="h-3 w-3 text-gray-400" />
                            {fee.amount}% of {fee.percentage_of?.replace('_', ' ')}
                          </>
                        ) : (
                          <>
                            <DollarSign className="h-3 w-3 text-gray-400" />
                            {formatCurrency(fee.amount)}
                          </>
                        )}
                      </span>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPaidByBadgeClass(fee.paid_by)}`}
                      >
                        {fee.paid_by.charAt(0).toUpperCase() + fee.paid_by.slice(1)} Paid
                      </span>
                      {fee.cd_section && (
                        <Badge variant="outline" className="text-xs">
                          Section {fee.cd_section}
                        </Badge>
                      )}
                    </div>
                    {fee.description && (
                      <div className="mt-1 text-xs text-gray-500">{fee.description}</div>
                    )}
                  </div>

                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(fee)}>
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(fee.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingFee ? 'Edit Default Fee' : 'Add Default Fee'}</DialogTitle>
            <DialogDescription>
              {editingFee
                ? 'Update the default fee configuration'
                : 'Add a default fee that will be auto-populated for this company'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="fee_name">Fee Name *</Label>
              <Input
                id="fee_name"
                value={formData.fee_name}
                onChange={(e) => handleInputChange('fee_name', e.target.value)}
                placeholder="e.g., Processing Fee"
                required
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="is_percentage"
                checked={formData.is_percentage}
                onCheckedChange={(checked: boolean) => handleInputChange('is_percentage', checked)}
              />
              <Label htmlFor="is_percentage">Percentage-based fee</Label>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="amount">{formData.is_percentage ? 'Percentage' : 'Amount'}</Label>
                <div className="relative">
                  <Input
                    id="amount"
                    type="number"
                    step={formData.is_percentage ? '0.01' : '0.01'}
                    min="0"
                    value={formData.amount ?? ''}
                    onChange={(e) =>
                      handleInputChange(
                        'amount',
                        e.target.value ? Number.parseFloat(e.target.value) : undefined,
                      )
                    }
                    className={formData.is_percentage ? '' : 'pl-6'}
                    placeholder={formData.is_percentage ? '1.0' : '500.00'}
                  />
                  {!formData.is_percentage && (
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500">
                      $
                    </span>
                  )}
                </div>
              </div>

              {formData.is_percentage && (
                <div>
                  <Label htmlFor="percentage_of">Percentage Of</Label>
                  <Select
                    value={formData.percentage_of || ''}
                    onValueChange={(value) => handleInputChange('percentage_of', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select base" />
                    </SelectTrigger>
                    <SelectContent>
                      {PERCENTAGE_OF_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="paid_by">Paid By *</Label>
                <Select
                  value={formData.paid_by}
                  onValueChange={(value: FeePaidBy) => handleInputChange('paid_by', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAID_BY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="cd_section">CD Section</Label>
                <Select
                  value={formData.cd_section || ''}
                  onValueChange={(value: CDSection) => handleInputChange('cd_section', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select section" />
                  </SelectTrigger>
                  <SelectContent>
                    {CD_SECTION_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Optional description..."
                rows={2}
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked: boolean) => handleInputChange('is_active', checked)}
              />
              <Label htmlFor="is_active">Active</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={createFee.isPending || updateFee.isPending}>
                {editingFee ? 'Save Changes' : 'Add Fee'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
